class CauseSelector extends HTMLElement {
	constructor() {
		super();
		this.openBtn = this.querySelector('.cusa-cause-selector-btn');
		this.closeBtn = this.querySelector('.cusa-cause-selector-close-btn');
		this.openBtnCaretWrapper = this.querySelector('.cusa-cause-selector-btn-caret-wrapper');

		this.dialogEl = this.querySelector('.cusa-cause-selector-dialog');
		this.causeLabels = [...this.querySelectorAll('.cusa-cause-selector-label')];
		this.causeRadioBtns = [...this.querySelectorAll('.cusa-cause-selector-radio')];

		this.productTour = this.querySelector('.cusa-cause-selector-product-tour');
		this.productTourBtn = this.querySelector('.cusa-cause-selector-info-btn');
		this.productTourSteps = [...this.querySelectorAll('.cusa-cause-selector-step')];
		this.productTourStepBns = [...this.querySelectorAll('.step-button')];

		this.siteName = this.dataset.siteName;
	}

	connectedCallback() {
		let causeInitialized = false;

		const waitForTrackingReady = () => {
			if (!causeInitialized && window.cusaCartAttributes?._cusa_click_tracking_is_active) {
				causeInitialized = true;
				this.setInitialSelectedCause();
			} else if (!causeInitialized) {
				setTimeout(waitForTrackingReady, 50);
			}
		};

		// Start polling
		waitForTrackingReady();

		setTimeout(() => {
			if (!causeInitialized) {
				console.warn('Forcing initial cause selection after timeout.');
				this.setInitialSelectedCause();
			}
		}, 2000);

		// Catch early cart readiness via event
		window.addEventListener(
			'cusa-tracking-ready',
			() => {
				waitForTrackingReady();
			},
			{ once: true }
		);

		this.openBtn.addEventListener('click', this.handleCauseSelectorBtnClick);
		this.closeBtn.addEventListener('click', this.closeCauseSelector);
		this.dialogEl.addEventListener('submit', (e) => e.preventDefault());
		this.dialogEl.addEventListener('keydown', this.handleKeyDown);
		this.causeLabels.map((labelEl) => {
			labelEl.addEventListener('click', this.handleLabelClick);
		});
		window.addEventListener('click', this.clickOutsideDialog);

		this.productTourBtn.addEventListener('click', this.handleProductTourBtnClick);
		this.productTourStepBns.map((btn) => {
			btn.addEventListener('click', this.handleStepBtnClick);
		});

		publish(PUB_SUB_EVENTS.updateRoyalty, this.getSelectedCauseRadioEl());
	}

	disconnectedCallback() {}

	/**
	 * Tracks clicks outside of the dialog box and the open/close btn. If detected,
	 * and the dialog is open, this closes the dialog
	 * @param {Event} e Aceepts a click event
	 * @returns null when a click happens on the selector btn or within the dialog
	 */
	clickOutsideDialog = (e) => {
		if (
			e.target.classList.contains('cusa-cause-selector-btn') ||
			e.target.classList.contains('cusa-cause-selector-info-btn') ||
			this.dialogEl.contains(e.target)
		)
			return;
		const dialogDimensions = this.dialogEl.getBoundingClientRect();
		const causeSelectorBtn = this.openBtn.getBoundingClientRect();
		const productTourBtn = this.productTourBtn.getBoundingClientRect();
		if (this.dialogEl.open) {
			if (
				(e.clientX < dialogDimensions.left ||
					e.clientX > dialogDimensions.right ||
					e.clientY < dialogDimensions.top ||
					e.clientY > dialogDimensions.bottom) &&
				(e.clientX < causeSelectorBtn.left ||
					e.clientX > causeSelectorBtn.right ||
					e.clientY < causeSelectorBtn.top ||
					e.clientY > causeSelectorBtn.bottom) &&
				(e.clientX < productTourBtn.left ||
					e.clientX > productTourBtn.right ||
					e.clientY < productTourBtn.top ||
					e.clientY > productTourBtn.bottom)
			) {
				this.dialogEl.close();
			}
		}
	};

	/**
	 * Open/close cause selector when the cause selector button is clicked
	 */
	handleCauseSelectorBtnClick = () => {
		this.dialogEl.open ? this.closeCauseSelector() : this.openCauseSelector();
	};

	/**
	 * Open/close cause selector when the product tour button is clicked
	 */
	handleProductTourBtnClick = () => {
		!this.dialogEl.open ? this.openCauseSelector() : null;
		const visibleStep = this.productTourSteps.filter((step) => !step.classList.contains('hidden'))[0];
		if (visibleStep) {
			return;
		}
		setTimeout(() => {
			this.productTourSteps[0].classList.remove('hidden');
		}, 0);
	};

	/**
	 * Handles clicks on the next and previous step buttons. Also accounts for
	 * clicking the last button which sets a local storage value to prevent
	 * seeing the product tour again on page load.
	 * @param {Event} e Accepts click event
	 * @returns null when final acceptance button is clicked, otherwise nothing
	 */
	handleStepBtnClick = (e) => {
		if (e.target.classList.contains('accept-btn')) {
			e.target.closest('.cusa-cause-selector-step').classList.add('hidden');
			setLocalStorage('hideCauseSelectorTour', 'true');
			return;
		}
		const currentStep = e.target.closest('.cusa-cause-selector-step');
		let otherStepNum = 0;
		e.target.classList.contains('next-step')
			? (otherStepNum = parseInt(currentStep.dataset.step) + 1)
			: (otherStepNum = parseInt(currentStep.dataset.step) - 1);
		const otherStep = document.querySelector(`[data-step="${otherStepNum}"]`);
		if (otherStep) {
			currentStep.classList.add('hidden');
			otherStep.classList.remove('hidden');
			if (e.type !== 'keydown') return;
			if (otherStepNum < this.productTourSteps.length) {
				otherStep.querySelector('.next-step').focus();
			} else {
				otherStep.querySelector('.prev-step').focus();
			}
		}
	};

	/**
	 * Toggles the caret orientation between pointing up and down
	 */
	updateCaret = () => {
		this.openBtnCaretWrapper.classList.toggle('open');
	};

	/**
	 * Opens the cause selector
	 */
	openCauseSelector = () => {
		!this.dialogEl.open ? this.dialogEl.show() : null;
		this.updateCaret();
	};

	/**
	 * Closes the cause selector
	 */
	closeCauseSelector = () => {
		this.dialogEl.open ? this.dialogEl.close() : null;
		[...this.dialogEl.querySelectorAll('.cusa-cause-selector-step')].map((el) => {
			!el.classList.contains('hidden') ? el.classList.add('hidden') : null;
		});
		this.updateCaret();
	};

	/**
	 * Handles keydown events when inside of the cause selector dialog or
	 * the open/close btn
	 * @param {Event} e Takes the click event
	 */
	handleKeyDown = (e) => {
		e.code === 'Escape' || e.code === 'Esc' ? this.closeCauseSelector() : null;
		if (e.code === 'Space' || e.code === ' ' || e.code === 'Enter' || e.code === 'Return') {
			e.preventDefault();
			if (document.activeElement === this.closeBtn) {
				this.closeCauseSelector();
			}
			if (e.target.classList.contains('step-button')) {
				this.handleStepBtnClick(e);
			}
			this.handleLabelClick();
		}
	};

	/**
	 * Handles click events on labels within the dialog box
	 */
	handleLabelClick = () => {
		const clickTimer = setTimeout(() => {
			const labelText = this.getLabelText();
			const causeHandle = this.getSelectedCauseRadioEl().id.split('-radio')[0];
			this.updateOpenBtn(labelText, this.getSVG());
			setLocalStorage('selected-cause', causeHandle);
			publish(PUB_SUB_EVENTS.updateRoyalty, causeHandle);
			clearTimeout(clickTimer);
		}, 0);
	};

	/**
	 * Gets the currently checked radio button within the cause selector
	 * @returns the checked radio button within the cause form
	 */
	getSelectedCauseRadioEl = () => {
		return this.dialogEl.querySelector('input[name="selected-cause"]:checked');
	};

	/**
	 * Gets the string inside of the clicked label within the cause selector
	 * @returns the string within a clicked cause label
	 */
	getLabelText = () => {
		return this.getSelectedCauseRadioEl()
			.closest('.cusa-cause-selector-input-group')
			.querySelector('.cusa-cause-selector-name').textContent;
	};

	/**
	 * Gets the svg code inside of a cause selector label
	 * @returns the svg for a label's icon
	 */
	getSVG = () => {
		return this.getSelectedCauseRadioEl()
			.closest('.cusa-cause-selector-input-group')
			.querySelector('.cusa-cause-selector-icon').innerHTML;
	};

	/**
	 * Updates the cause selector button to match the currently selected cause
	 * in the dialog form.
	 * -- IMPORTANT! --
	 * This function is also where the "Cause" cart attribute is updated.
	 * @param {String} selectedName Accepts a string derived from the label value
	 * @param {HTMLElement} svgCode Accepts the inner html of the svg wrapper within the clicked label
	 */
	updateOpenBtn = (selectedName, svgCode) => {
		this.openBtn.querySelector('.cusa-cause-selector-btn-text').textContent = selectedName;
		this.openBtn.querySelector('.cusa-cause-selector-btn-icon').innerHTML = svgCode;
		this.debouncedSetCauseCartAttribute(selectedName);
	};

	/**
	 * Sets the `checked` attribute of a radio button to true if it is found.
	 * Otherwise it throws an error.
	 * @param {String} selectedCauseId Takes the ID of the clicked radio btn
	 */
	setSelectedCauseRadioToChecked = (selectedCauseId) => {
		try {
			document.getElementById(selectedCauseId).checked = true;
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * Checks for a 'selected-cause' url parameter and if found, returns the value
	 * @returns {?string} selected-cause parameter value, if present
	 */
	checkForCauseParam = () => {
		return new URLSearchParams(window.location.search).get('selected-cause');
	};

	/**
	 * This uses the cart api to add a Cause and a Site value as cart attributes.
	 * There is also logic to only do so if the local storage value for
	 * "selected-cause-name" is null or different than what is passed at the time of
	 * function call. This avoids needless calls to the api on pageload and repeated
	 * cause option click.
	 * @param {String} selectedName The human readable name of the selected cause.
	 * This is obtained from the cause name displayed in the cause selector button.
	 * This method registers the update using `registerCartAttributeUpdate` to safely batch writes via a centralized cart update system.
	 */
	setCauseCartAttribute = async (selectedName) => {
		if (!window.cusaCartAttributes?._cusa_click_tracking_is_active) {
			console.warn('Tracking not yet initialized. Skipping cart attribute write.');
			return;
		}

		registerCartAttributeUpdate({
			Cause: selectedName,
			Site: this.siteName
		});

		if (selectedName !== getLocalStorage('selected-cause-name')) {
			setLocalStorage('selected-cause-name', selectedName);
		}

		console.log(`Cause update registered: ${selectedName}`);
		console.log(`Site update registered: ${this.siteName}`);
	};

	/**
	 * A debounced version of the setCauseCartAttribute method to prevent spamming
	 * the /cart/update.js endpoint with updates if users click cause options rapidly
	 */
	debouncedSetCauseCartAttribute = debounce((selectedName) => {
		const storedCause = window.cusaCartAttributes?.Cause;
		const cartCauseMismatch = storedCause && storedCause !== selectedName;
		if (selectedName !== getLocalStorage('selected-cause-name') || cartCauseMismatch) {
			this.setCauseCartAttribute(selectedName);
		}
	}, FETCH_DEBOUNCE_TIMER);

	/**
	 * Sets the selected cause on page load. This first checks for a 'selected-cause'
	 * parameter and uses that if a corresponding radio button is found in the cause
	 * selector's form. If not, it checks for a stored value in local storage. If
	 * nothing is found there, it selects the first cause in the list of causes.
	 * The "Cause" cart attribute is also set using the cause selector button text.
	 */
	setInitialSelectedCause = () => {
		// suggested refactor for the below:
		// let selectedCause = this.checkForCauseParam() ?? getLocalStorage('selected-cause');
		// const radioId = selectedCause ? `${selectedCause}-radio` : this.causeRadioBtns[0].id;
		// if (!selectedCause) selectedCause = radioId.split('-radio')[0];

		let selectedCause = this.checkForCauseParam();
		let radioId = `${selectedCause}-radio`;
		if (!document.getElementById(radioId)) {
			selectedCause = getLocalStorage('selected-cause');
			if (selectedCause && document.getElementById(`${selectedCause}-radio`)) {
				radioId = `${selectedCause}-radio`;
			} else {
				radioId = this.causeRadioBtns[0].id;
				selectedCause = radioId.split('-radio')[0];
			}
		}

		setLocalStorage('selected-cause', selectedCause);
		this.setSelectedCauseRadioToChecked(radioId);
		this.updateOpenBtn(this.getLabelText(), this.getSVG());
		const pubTimer = setTimeout(() => {
			publish(PUB_SUB_EVENTS.updateRoyalty, selectedCause);
			clearTimeout(pubTimer);
		}, 150);
	};
}

customElements.define('cusa-cause-selector', CauseSelector);
